import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { updateUnauthTripUserDetails } from '@packages/api';
import { isUSRegionSelector, useConfigStore, useRegionStore } from '@packages/stores';
import { GuestDetailsForm, ManualAddressFormInterface } from '@packages/types';
import { constructAddressData, getEmcBoost, shouldShowBlankDob } from '@packages/utils';

import { captureException } from '@sentry/react';
import {
  CheckoutSectionType,
  getTripSelector,
  useCheckoutStore,
  useEmcStore,
  useGuestStore,
  useTripStore,
} from '@store';
import { isManualAddressSectionOpenSelector } from '@store/checkout/checkout.selectors';
import {
  hasTravellerErrors,
  shouldUpdateTrip,
  validateEmcTravellers,
  willPriceChange,
} from '@utils';

import { usePriceChangeModal } from '../usePriceChangeModal';

export const useCheckoutFlow = () => {
  // Trip Store
  const syncTrip = useTripStore(state => state.syncTrip);
  const setTrip = useTripStore(state => state.setTrip);
  const isTripPending = useTripStore(state => state.pending);
  const trip = useTripStore(getTripSelector);

  const isManualAddressSectionOpen = useCheckoutStore(isManualAddressSectionOpenSelector);
  const setGuest = useGuestStore(state => state.setGuest);
  const { openPriceChangeModal } = usePriceChangeModal();
  const countryName = useConfigStore(state => state?.regionSpecificConfig?.NAME) ?? '';
  const countryCode = useConfigStore(state => state?.regionSpecificConfig?.CODE) ?? '';
  const region = useRegionStore(state => state.region);
  const methods = useFormContext();
  const setOpenSection = useCheckoutStore(state => state.setOpenSection);
  const setDisplaySummaryDetails = useCheckoutStore(state => state.setDisplaySummaryDetails);
  const isLegalConsentChecked = useCheckoutStore(state => state.isLegalConsentChecked);
  const openSection = useCheckoutStore(state => state.openSection);
  const setHasLegalConsentError = useCheckoutStore(state => state.setHasLegalConsentError);
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  const emcBoost = getEmcBoost(trip);
  const setErrorTravellers = useEmcStore(state => state.setErrorTravellers);
  const nameChangedTravellers = useEmcStore(state => state.nameChangedTravellers);
  const setShowEmcErrorsOnContinue = useEmcStore(state => state.setShowEmcErrorsOnContinue);
  const setHasConfirmedAssessment = useCheckoutStore(state => state.setHasConfirmedAssessment);
  const resetNameChangedTraveller = useEmcStore(state => state.resetNameChangedTraveller);
  const isUS = useRegionStore(isUSRegionSelector);

  const submitTravellerDetails = useCallback(
    async (values: GuestDetailsForm) => {
      const manualAddress: ManualAddressFormInterface = {
        addressCity: values?.addressCity,
        addressLine1: values?.addressLine1,
        addressLine2: values?.addressLine2?.trim(),
        addressPostCode: values?.addressPostCode,
        addressState: values?.addressState,
      };

      const addressData = isManualAddressSectionOpen
        ? constructAddressData({
            formData: manualAddress,
            countryCode,
            countryName,
            country: region?.country,
          })
        : values?.addressData;
      const diffInTrip = shouldUpdateTrip({ guestDetails: values, trip });

      if (willPriceChange(diffInTrip, trip)) {
        openPriceChangeModal({
          onNext: async () => {
            setTrip({
              ...trip,
              ...(diffInTrip?.primaryTraveller && {
                primaryTraveller: diffInTrip.primaryTraveller,
              }),
              ...(diffInTrip?.secondaryTravellers && {
                secondaryTravellers: diffInTrip.secondaryTravellers,
              }),
            });
            resetNameChangedTraveller();
            if (addressData)
              setGuest({ ...values, email: values.email?.toLowerCase(), addressData });
            await syncTrip();
          },
        });
        return 'priceChangeModal';
      }

      if (shouldShowBlankDob(trip.primaryTraveller)) {
        setTrip({
          ...trip,
          ...(diffInTrip?.primaryTraveller && {
            primaryTraveller: diffInTrip.primaryTraveller,
          }),
          ...(diffInTrip?.secondaryTravellers && {
            secondaryTravellers: diffInTrip.secondaryTravellers,
          }),
        });
        resetNameChangedTraveller();
        if (addressData) {
          setGuest({ ...values, email: values.email?.toLowerCase(), addressData });
        }
        await syncTrip({ quiet: true });
        return;
      }

      if (addressData) setGuest({ ...values, email: values.email?.toLowerCase(), addressData });
    },
    [
      countryCode,
      countryName,
      isManualAddressSectionOpen,
      openPriceChangeModal,
      region?.country,
      resetNameChangedTraveller,
      setGuest,
      setTrip,
      syncTrip,
      trip,
    ],
  );

  const shouldShowPriceModal = useCallback(
    (values: GuestDetailsForm) => {
      const diffInTrip = shouldUpdateTrip({ guestDetails: values, trip });

      if (willPriceChange(diffInTrip, trip)) {
        openPriceChangeModal({
          onNext: async () => {
            setTrip({
              ...trip,
              ...(diffInTrip?.primaryTraveller && {
                primaryTraveller: diffInTrip.primaryTraveller,
              }),
              ...(diffInTrip?.secondaryTravellers && {
                secondaryTravellers: diffInTrip.secondaryTravellers,
              }),
            });
            setGuest({ ...values, email: values.email?.toLowerCase() });
            resetNameChangedTraveller();
            await syncTrip();
          },
        });
        return 'priceChangeModal';
      }
    },
    [openPriceChangeModal, resetNameChangedTraveller, setGuest, setTrip, syncTrip, trip],
  );

  const onCheckoutProgress = useCallback(
    async (type?: CheckoutSectionType, onValid?: () => void | Promise<void>) => {
      await methods?.handleSubmit(
        async (value: GuestDetailsForm) => {
          if (isTripPending) return;
          const checkoutResult = await submitTravellerDetails(value);

          setDisplaySummaryDetails(true);
          if (checkoutResult === 'priceChangeModal') {
            return;
          }
          // validate yes no button for emc
          const errors = validateEmcTravellers(emcBoost, value);

          if (hasTravellerErrors(errors)) {
            setErrorTravellers(errors);
            setShowEmcErrorsOnContinue(true);
            return;
          }

          //update emc traveller error
          setErrorTravellers(errors);

          //validate confirm assessment
          if (nameChangedTravellers && nameChangedTravellers?.length > 0) {
            setHasConfirmedAssessment(false);
            return;
          }

          if (!isLegalConsentChecked && type !== 'travellerDetails') {
            if (openSection === 'legalStuff') {
              setHasLegalConsentError(true);
            }

            //start of saving trip traveller details
            if (
              value?.addressData &&
              value?.dob &&
              value?.firstName &&
              value?.lastName &&
              value?.email &&
              trip?.sortKey &&
              trip?.userId &&
              value?.addressData?.detail?.address_components
            ) {
              updateUnauthTripUserDetails({
                primaryTravellerDetails: {
                  firstName: value.firstName,
                  lastName: value.lastName,
                  email: value.email?.toLowerCase(),
                  address: value.addressData?.data?.description,
                  addressDetail: value.addressData.detail?.address_components,
                  userId: trip?.userId,
                  dob: value.dob,
                },
                region: { country: 'AU' },
                tripId: trip.sortKey,
              }).catch(error =>
                captureException(error, {
                  tags: { source: 'useCheckoutFlow', event: 'updateUnauthTripUserDetails' },
                }),
              );
            }
            //end of saving trip traveller details
            return setOpenSection('legalStuff');
          }

          const sectionToOpen = openSection === 'travellerDetails' ? 'legalStuff' : 'payment';
          if (type === 'payment' || sectionToOpen === 'payment') {
            window.scrollTo({ top: windowHeight * 0.75, behavior: 'smooth' });
          }

          if (type) {
            setOpenSection(type);
          } else {
            setOpenSection(sectionToOpen);
          }
          await onValid?.();
        },
        () => {
          // allows the user to open any section if they have not completed the payment form
          if (isUS && openSection === 'payment') {
            if (type) {
              setOpenSection(type);
            }
            return;
          }
          setOpenSection(openSection);
          const errors = validateEmcTravellers(emcBoost, methods.watch());
          // If the trip is pending, do not show the emc errors
          if (!isTripPending && hasTravellerErrors(errors)) {
            setErrorTravellers(errors);
            setShowEmcErrorsOnContinue(true);
            return;
          }
        },
      )();
    },
    [
      methods,
      isTripPending,
      submitTravellerDetails,
      setDisplaySummaryDetails,
      emcBoost,
      setErrorTravellers,
      nameChangedTravellers,
      isLegalConsentChecked,
      openSection,
      setShowEmcErrorsOnContinue,
      setHasConfirmedAssessment,
      trip.sortKey,
      trip?.userId,
      setOpenSection,
      setHasLegalConsentError,
      windowHeight,
      isUS,
    ],
  );

  return {
    onCheckoutProgress,
    shouldShowPriceModal,
  };
};
